import { toast } from "react-toastify";
import { useReportsStore } from "../../store/reports/reportsStore";
import api from "../../utils/api";
import { removeLiteNotificationFromCache } from "../../features/notifications/liteQueries";

export interface CustomNotificationProps {
  title: string;
  body: string;
  image: string;
  url: string;
  icon: string;
  type: "report" | "notification";
  /** ID del documento de notificación (para marcar como leído en BD) */
  notificationId: string;
  /** ID del job de reporte (solo tipo "report"; para readedReport) */
  reportId?: string;
}

const NOTIFICATION_ICONS = [
  {
    id: "warning-red",
    icon: "ri-error-warning-fill",
    color: "text-red-500",
    bgSelected: "bg-red-500/20",
    ringColor: "ring-red-500",
    borderColor: "border-red-500",
  },
  {
    id: "warning-yellow",
    icon: "ri-alert-fill",
    color: "text-yellow-500",
    bgSelected: "bg-yellow-500/20",
    ringColor: "ring-yellow-500",
    borderColor: "border-yellow-500",
  },
  {
    id: "chat-blue",
    icon: "ri-chat-3-fill",
    color: "text-blue-500",
    bgSelected: "bg-blue-500/20",
    ringColor: "ring-blue-500",
    borderColor: "border-blue-500",
  },
  {
    id: "success-green",
    icon: "ri-checkbox-circle-fill",
    color: "text-green-500",
    bgSelected: "bg-green-500/20",
    ringColor: "ring-green-500",
    borderColor: "border-green-500",
  },
];

const getIconData = (icon: string) => {
  return (
    NOTIFICATION_ICONS.find((i) => i.id === icon) || {
      icon: "ri-notification-line",
      color: "text-gray-500",
      bgColor: "bg-gray-500/10",
    }
  );
};

export const CustomNotification = ({ title, body, image, url, icon, type = "notification", notificationId, reportId }: CustomNotificationProps) => {
  const handleClose = () => {
    toast.dismiss();
  };

  const iconData = getIconData(icon);
  const readedReport = useReportsStore((state) => state.readedReport);

  const markNotificationAsRead = async () => {
    try {
      if (notificationId) {
        await api.put("/notifications-read", { notification_id: notificationId });
        removeLiteNotificationFromCache(notificationId);
      } else if (type === "report" && reportId) {
        await api.put("/notifications-read", { report_id: reportId });
      }
    } catch (err) {
      console.warn("Error marking notification as read:", err);
    }
  };

  const handleViewMore = async () => {
    await markNotificationAsRead();
    if (type === "report" && reportId) {
      await readedReport(reportId);
    }

    const isExternal = url.startsWith("http") && !url.startsWith(window.location.origin);
    if (isExternal) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      const path = url.startsWith("http") ? new URL(url).pathname : url;
      window.location.href = path;
    }
    handleClose();
  };

  return (
    <div className="">
      {image && (
        <img
          src={image}
          alt="Notificación"
          className="w-full h-40 object-cover rounded-t-lg max-sm:hidden border border-gray-200 rounded-lg"
        />
      )}
      <div className="p-3">
        <div className="flex items-center space-x-2">
          <i className={`${iconData.icon} text-lg ${iconData.color}`}></i>
          <h4 className="m-0 text-base font-semibold text-gray-800">{title}</h4>
        </div>
        <p className="m-0 mb-2.5 text-sm text-gray-600 mt-1">{body}</p>

        <div className="flex justify-between mt-3">
          <button
            onClick={handleViewMore}
            className="bg-primary-500 hover:bg-primary-600 text-white rounded px-3 py-1.5 text-sm transition-colors"
          >
            Ver más
          </button>
          <button
            onClick={handleClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded px-3 py-1.5 text-sm transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};